import { logger, type Basket, type CareOrder, type FundDefinition } from '@talos/kyoko';

export const CARE_ORDER_FORM_FORMGROUP_MB = 'spacing16';

/**
 *  Given a CareOrder and the current list of baskets, will figure out if the BasketRatioDate on the CareOrder (if any)
 * is outdated relative to the current basket definition for the same fund.
 *
 * If the CareOrder is not a Unit-based order, or we for some reason fail to parse/compute/compare the output, false is returned.
 */
export function isBasketRatioChanged(careOrder: CareOrder | undefined, baskets: Basket[]): boolean {
  const orderBasketSymbol = careOrder?.basketOrderDetails?.BasketSymbol;
  const orderBasketRatio = careOrder?.basketOrderDetails?.BasketRatio;
  const orderBasketRatioDateStr = careOrder?.basketOrderDetails?.BasketRatioDate;
  if (!orderBasketSymbol || !orderBasketRatio || !orderBasketRatioDateStr) {
    return false;
  }

  const currentBasket = baskets.find(b => b.BasketSymbol === orderBasketSymbol);
  if (!currentBasket) {
    // There exists basket details on the CO, but none in the system, so it has changed
    return true;
  }

  const currentBasketRatio = currentBasket.QuantityDetails.BasketRatio;
  if (orderBasketRatio !== currentBasketRatio) {
    // The ratio itself (numeric) has changed
    return true;
  }

  // Below here is date checking (only MDY in UTC)
  const currentBasketRatioDateStr = currentBasket.QuantityDetails.BasketRatioDate;
  try {
    // For now we only care about the Y, M, D in UTC terms and check that they are the same
    const d1 = new Date(orderBasketRatioDateStr);
    const d2 = new Date(currentBasketRatioDateStr);
    const isSameDay =
      d1.getUTCFullYear() === d2.getUTCFullYear() &&
      d1.getUTCMonth() === d2.getUTCMonth() &&
      d1.getUTCDate() === d2.getUTCDate();
    return !isSameDay;
  } catch (e) {
    logger.error(new Error('Failed to evalute isBasketRatioDateOutdated'), {
      extra: { currentBasketRatioDateStr, orderBasketRatioDateStr },
    });

    return false;
  }
}

/**
 *  When working with Care Orders we often only care about the Date portion. However, we still want to send complete ISO timestamps.
 * This means that we need to set some time component on them. The standard is 10AM UTC time.
 */
export function convertDateToDefaultCareOrderUTCDate(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 10, 0, 0));
}

/** A fund is selectable if it has tradable securities defined and with at least one entry */
export function isFundSelectableInCareOrderForm(fund: FundDefinition): boolean {
  return fund.TradableSecurities != null && fund.TradableSecurities.length > 0;
}
